import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SMSPageLayout } from './SMSPageLayout';
import { fetchSmsLogs, fetchSmsLogsRealTime, calculateStats, downloadLogsAsCSV, SmsLogEntry, SmsLogsFilters, fetchCompanyUsers, CompanyUserOption, fetchAllUsers, fetchSmsLogsStats, SmsLogsStats } from '../../services/SmsLogsService';
import { useNavigation } from '../../contexts/NavigationContext';
import { userCan } from '../../utils/permissions';

export const SMSLogsPage: React.FC = () => {
  const { user } = useNavigation();
  const [filters, setFilters] = useState<SmsLogsFilters>({ page: 1, limit: 25 });
  const [logs, setLogs] = useState<SmsLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<SmsLogsStats>({ total: 0, delivered: 0, failed: 0, pending: 0, sent: 0, deliveryRate: 0 });
  const [statsLoading, setStatsLoading] = useState(false);

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshIntervalMs, setRefreshIntervalMs] = useState(10000);
  const lastUpdatedRef = useRef<string | null>(null);
  // Role helpers
  const role = user?.role;
  const isSuperAdmin = role === 'super_admin';
  const canManageCompanyUsers = userCan(user, 'user_management', 'read') && (role === 'admin' || role === 'super_admin');
  const [companyUsers, setCompanyUsers] = useState<CompanyUserOption[]>([]);
  const [companyUsersError, setCompanyUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<CompanyUserOption[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchDropdownOpen, setUserSearchDropdownOpen] = useState(false);
  const userSearchTimeout = useRef<number | undefined>(undefined);
  const userSearchInputRef = useRef<HTMLInputElement>(null);
  const userSearchDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadCompanyUsers() {
      if (!canManageCompanyUsers) return;
      try {
        const list = await fetchCompanyUsers();
        setCompanyUsers(list);
      } catch (e: any) {
        setCompanyUsersError(e?.message || 'Failed to load company users');
      }
    }
    loadCompanyUsers();
  }, [canManageCompanyUsers]);

  // Super admin user search (debounced)
  useEffect(() => {
    if (!isSuperAdmin || !userSearchDropdownOpen) return;
    
    // If dropdown just opened and search is empty, load initial users immediately
    if (!userSearch) {
      setUserSearchLoading(true);
      fetchAllUsers({ page: 1, limit: 20 })
        .then(({ users }) => {
          setUserSearchResults(users);
        })
        .catch(() => {
          setUserSearchResults([]);
        })
        .finally(() => {
          setUserSearchLoading(false);
        });
      return;
    }
    
    // Otherwise, debounce the search
    if (userSearchTimeout.current) window.clearTimeout(userSearchTimeout.current);
    setUserSearchLoading(true);
    // @ts-ignore - Window typing for setTimeout id across browsers
    userSearchTimeout.current = window.setTimeout(async () => {
      try {
        const { users } = await fetchAllUsers({ q: userSearch, page: 1, limit: 20 });
        setUserSearchResults(users);
      } catch (_) {
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
    return () => {
      if (userSearchTimeout.current) window.clearTimeout(userSearchTimeout.current);
    };
  }, [isSuperAdmin, userSearch, userSearchDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!userSearchDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userSearchInputRef.current &&
        userSearchDropdownRef.current &&
        !userSearchInputRef.current.contains(event.target as Node) &&
        !userSearchDropdownRef.current.contains(event.target as Node)
      ) {
        setUserSearchDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userSearchDropdownOpen]);

  // Load stats on mount and when userId filter changes
  useEffect(() => {
    async function loadStats() {
      setStatsLoading(true);
      try {
        const statsData = await fetchSmsLogsStats(filters.userId);
        setStats(statsData);
      } catch (e: any) {
        console.error('Failed to load stats:', e);
        // Don't show error to user, just use default stats
        setStats({ total: 0, delivered: 0, failed: 0, pending: 0, sent: 0, deliveryRate: 0 });
      } finally {
        setStatsLoading(false);
      }
    }
    loadStats();
  }, [filters.userId]);

  // Review state
  const [showReview, setShowReview] = useState(false);
  const [reviewLogs, setReviewLogs] = useState<SmsLogEntry[]>([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);

  function updateFilter<K extends keyof SmsLogsFilters>(key: K, value: SmsLogsFilters[K]) {
    setFilters(prev => ({ ...prev, page: key === 'page' ? (value as number) : 1, [key]: value }));
  }

  async function loadLogs() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSmsLogs(filters);
      setLogs(res.data);
      setTotal(res.pagination.total);
      // Initialize lastUpdatedRef with the newest timestamp, or current time if no logs
      if (res.data.length > 0) {
        const newest = res.data[0]?.recipient_updated_at || res.data[0]?.recipient_created_at;
        if (newest) {
          // Use timestamp directly from database (format: "YYYY-MM-DD HH:MM:SS")
          lastUpdatedRef.current = newest;
        } else if (!lastUpdatedRef.current) {
          // Fallback: use current time in MySQL DATETIME format
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          lastUpdatedRef.current = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
      } else if (!lastUpdatedRef.current) {
        // If no logs exist, initialize with current time so polling can start
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        lastUpdatedRef.current = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }

  // Initial and filter-driven load
  useEffect(() => {
    loadLogs();
  }, [filters.status, filters.phone, filters.senderId, filters.startDate, filters.endDate, filters.startTime, filters.endTime, filters.q, filters.page, filters.limit, filters.userId]);

  // Polling for real-time updates
  useEffect(() => {
    if (!autoRefresh) return;
    
    // Initialize lastUpdatedRef if not set (MySQL DATETIME format: "YYYY-MM-DD HH:MM:SS")
    if (!lastUpdatedRef.current) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      lastUpdatedRef.current = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    
    const id = setInterval(async () => {
      try {
        if (!lastUpdatedRef.current) {
          // Fallback initialization
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          lastUpdatedRef.current = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
        const res = await fetchSmsLogsRealTime(lastUpdatedRef.current, { ...filters, page: 1 });
        if (res.data.length > 0) {
          // Merge new/updated items based on recipient_id uniqueness
          setLogs(prevLogs => {
            const existingById = new Map(prevLogs.map(l => [l.recipient_id, l]));
            for (const item of res.data) {
              existingById.set(item.recipient_id, item);
            }
            const merged = Array.from(existingById.values()).sort((a, b) => {
              const aTime = a.recipient_updated_at || a.recipient_created_at;
              const bTime = b.recipient_updated_at || b.recipient_created_at;
              return aTime < bTime ? 1 : -1;
            });
            return merged.slice(0, filters.limit || 25);
          });
          // Update lastUpdatedRef with the newest timestamp
          const newest = res.data[0]?.recipient_updated_at || res.data[0]?.recipient_created_at;
          if (newest) {
            lastUpdatedRef.current = newest;
          }

          // Also refresh 24-hour stats whenever new activity is detected
          try {
            setStatsLoading(true);
            const statsData = await fetchSmsLogsStats(filters.userId);
            setStats(statsData);
          } catch (e) {
            console.error('Failed to refresh stats during polling:', e);
          } finally {
            setStatsLoading(false);
          }
        }
      } catch (e) {
        // Silently fail polling errors to avoid console spam
        console.debug('Polling error:', e);
      }
    }, refreshIntervalMs);
    return () => clearInterval(id);
  }, [autoRefresh, refreshIntervalMs, filters.status, filters.phone, filters.senderId, filters.startDate, filters.endDate, filters.startTime, filters.endTime, filters.q, filters.userId, filters.limit]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (filters.limit || 25))), [total, filters.limit]);

  const statusBadge = (s: SmsLogEntry['status']) => {
    const color = s === 'delivered' ? 'bg-green-100 text-green-800' : s === 'failed' ? 'bg-red-100 text-red-800' : s === 'queued' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800';
    return <span className={`px-2 py-1 rounded text-xs font-semibold ${color}`}>{s}</span>;
  };

  // Review handlers
  async function handleReview() {
    setShowReview(true);
    setReviewLoading(true);
    setReviewPage(1);
    try {
      const res = await fetchSmsLogs({ ...filters, page: 1 });
      setReviewLogs(res.data);
      setReviewTotal(res.pagination.total);
    } catch (e: any) {
      setError(e?.message || 'Failed to load review logs');
    } finally {
      setReviewLoading(false);
    }
  }

  async function handleReviewPageChange(nextPage: number) {
    setReviewLoading(true);
    setReviewPage(nextPage);
    try {
      const res = await fetchSmsLogs({ ...filters, page: nextPage });
      setReviewLogs(res.data);
      setReviewTotal(res.pagination.total);
    } catch (e: any) {
      setError(e?.message || 'Failed to load review logs');
    } finally {
      setReviewLoading(false);
    }
  }

  // Download handlers
  async function fetchAllLogs(maxRows = 10000): Promise<SmsLogEntry[]> {
    const pageSize = Math.min(filters.limit || 100, 200);
    let page = 1;
    let all: SmsLogEntry[] = [];
    while (all.length < maxRows) {
      const res = await fetchSmsLogs({ ...filters, page, limit: pageSize });
      all = all.concat(res.data);
      const totalPages = Math.ceil(res.pagination.total / pageSize);
      if (page >= totalPages) break;
      page += 1;
    }
    if (all.length > maxRows) all = all.slice(0, maxRows);
    return all;
  }

  function downloadAsExcelCsv(data: SmsLogEntry[]) {
    // Use CSV content but with .xlsx filename for Excel compatibility
    const csvContent = [
      'Message ID,Recipient ID,Phone,Status,Sender ID,Username,Message Body,Char Count,Parts,Sent Time,Delivered Time,Failed Time,Error Message,Provider Message ID',
      ...data.map(log => {
        const sentTime = log.status !== 'queued' ? new Date(log.recipient_created_at).toISOString() : '';
        const deliveredTime = log.status === 'delivered' ? new Date(log.recipient_updated_at).toISOString() : '';
        const failedTime = log.status === 'failed' ? new Date(log.recipient_updated_at).toISOString() : '';
        const safeBody = `"${log.body.replace(/"/g, '""')}"`;
        const safeError = log.error ? `"${log.error.replace(/"/g, '""')}"` : '';
        return [
          log.message_id,
          log.recipient_id,
          log.phone,
          log.status,
          log.sender_id || '',
          log.username || '',
          safeBody,
          String(log.char_count),
          String(log.parts),
          sentTime,
          deliveredTime,
          failedTime,
          safeError,
          log.provider_message_id || ''
        ].join(',');
      })
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sms-logs-${new Date().toISOString().slice(0,10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function downloadAsPdfPrint(data: SmsLogEntry[]) {
    // Print-friendly HTML rendered in a new window; user can save as PDF
    const win = window.open('', '_blank');
    if (!win) return;
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
        th { background: #f5f5f5; }
      </style>
    `;
    const rows = data.map(d => `
      <tr>
        <td>${d.message_id}</td>
        <td>${d.recipient_id}</td>
        <td>${d.phone}</td>
        <td>${d.status}</td>
        <td>${d.sender_id || ''}</td>
        <td>${d.username || ''}</td>
        <td>${(d.body || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
        <td>${d.char_count}</td>
        <td>${d.parts}</td>
        <td>${d.recipient_created_at}</td>
        <td>${d.status === 'delivered' ? d.recipient_updated_at : ''}</td>
        <td>${d.status === 'failed' ? d.recipient_updated_at : ''}</td>
        <td>${(d.error || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
      </tr>
    `).join('');
    const html = `
      <html>
        <head>
          <title>SMS Logs</title>
          ${styles}
        </head>
        <body>
          <h3>SMS Logs Export</h3>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>
                <th>Message ID</th>
                <th>Recipient ID</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Sender ID</th>
                <th>Username</th>
                <th>Message Body</th>
                <th>Char Count</th>
                <th>Parts</th>
                <th>Sent Time</th>
                <th>Delivered Time</th>
                <th>Failed Time</th>
                <th>Error Message</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <script>window.onload = () => { window.print(); };<\/script>
        </body>
      </html>
    `;
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  async function handleDownload(format: 'csv' | 'pdf' | 'excel') {
    setDownloadMenuOpen(false);
    try {
      const data = await fetchAllLogs();
      if (format === 'csv') {
        downloadLogsAsCSV(data);
      } else if (format === 'excel') {
        downloadAsExcelCsv(data);
      } else if (format === 'pdf') {
        downloadAsPdfPrint(data);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to download logs');
    }
  }

  function handleResetFilters() {
    setFilters({ page: 1, limit: 25 });
    setUserSearch('');
    setUserSearchResults([]);
    setUserSearchDropdownOpen(false);
  }

  return (
    <SMSPageLayout title="SMS Logs">
      {/* Stats - Last 24 Hours */}
      <div className="mb-2 px-1">
        <h2 className="text-md font-bold">Statistics (Last 24 Hours)</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">Total</span>
            {statsLoading ? (
              <span className="text-2xl font-bold text-gray-400">Loading...</span>
            ) : (
              <span className="text-2xl font-bold">{stats.total}</span>
            )}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">Delivered</span>
            {statsLoading ? (
              <span className="text-2xl font-bold text-gray-400">Loading...</span>
            ) : (
              <span className="text-2xl font-bold">{stats.delivered}</span>
            )}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">Failed</span>
            {statsLoading ? (
              <span className="text-2xl font-bold text-gray-400">Loading...</span>
            ) : (
              <span className="text-2xl font-bold">{stats.failed}</span>
            )}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">Pending</span>
            {statsLoading ? (
              <span className="text-2xl font-bold text-gray-400">Loading...</span>
            ) : (
              <span className="text-2xl font-bold">{stats.pending}</span>
            )}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">Delivery Rate</span>
            {statsLoading ? (
              <span className="text-2xl font-bold text-gray-400">Loading...</span>
            ) : (
              <span className="text-2xl font-bold">{stats.deliveryRate}%</span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select className="w-full border rounded px-3 py-2 text-sm" value={filters.status || ''} onChange={e => updateFilter('status', (e.target.value || undefined) as any)}>
              <option value="">All</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="queued">Pending</option>
              <option value="sent">Sent</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Phone</label>
            <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Search phone" value={filters.phone || ''} onChange={e => updateFilter('phone', e.target.value || undefined)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Sender ID</label>
            <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Sender ID" value={filters.senderId || ''} onChange={e => updateFilter('senderId', e.target.value || undefined)} />
          </div>
          {canManageCompanyUsers && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">User</label>
              {!isSuperAdmin && (
                <>
                  <select className="w-full border rounded px-3 py-2 text-sm" value={filters.userId || ''} onChange={e => updateFilter('userId', (e.target.value || undefined) as any)}>
                    <option value="">All Users</option>
                    {companyUsers.map(u => (
                      <option key={u.id} value={u.id}>{`${u.firstName} ${u.lastName} (${u.email})`}</option>
                    ))}
                  </select>
                  {companyUsersError && <div className="text-xs text-red-600 mt-1">{companyUsersError}</div>}
                </>
              )}
              {isSuperAdmin && (
                <div className="relative">
                  <input 
                    ref={userSearchInputRef}
                    className="w-full border rounded px-3 py-2 text-sm" 
                    placeholder="Search users by name, email or company" 
                    value={userSearch} 
                    onChange={e => setUserSearch(e.target.value)}
                    onFocus={() => setUserSearchDropdownOpen(true)}
                  />
                  {userSearchDropdownOpen && (
                    <div ref={userSearchDropdownRef} className="absolute left-0 right-0 bg-white border rounded mt-1 max-h-60 overflow-auto z-10 shadow-lg">
                      {userSearchLoading && <div className="px-3 py-2 text-sm text-gray-600">Searching...</div>}
                      {!userSearchLoading && userSearchResults.length > 0 && userSearchResults.map(u => (
                        <button 
                          type="button" 
                          key={u.id} 
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100" 
                          onClick={() => {
                            updateFilter('userId', u.id);
                            setUserSearchDropdownOpen(false);
                            setUserSearch('');
                            setUserSearchResults([]);
                          }}
                        >
                          {`${u.firstName} ${u.lastName} (${u.email})`}{u.company ? ` • ${u.company}` : ''}
                        </button>
                      ))}
                      {!userSearchLoading && userSearch && userSearchResults.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-600">No results</div>
                      )}
                      {!userSearchLoading && !userSearch && userSearchResults.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-600">Start typing to search users</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Search Body</label>
            <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Contains text" value={filters.q || ''} onChange={e => updateFilter('q', e.target.value || undefined)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start Date</label>
            <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={filters.startDate || ''} onChange={e => updateFilter('startDate', e.target.value || undefined)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End Date</label>
            <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={filters.endDate || ''} onChange={e => updateFilter('endDate', e.target.value || undefined)} />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start Time</label>
            <input type="time" className="w-full border rounded px-3 py-2 text-sm" value={filters.startTime || ''} onChange={e => updateFilter('startTime', e.target.value || undefined)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End Time</label>
            <input type="time" className="w-full border rounded px-3 py-2 text-sm" value={filters.endTime || ''} onChange={e => updateFilter('endTime', e.target.value || undefined)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Per Page</label>
            <select className="w-full border rounded px-3 py-2 text-sm" value={filters.limit || 25} onChange={e => updateFilter('limit', parseInt(e.target.value, 10))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <label className="text-sm text-gray-700">Auto Refresh</label>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Interval</label>
            <select className="w-full border rounded px-3 py-2 text-sm" value={refreshIntervalMs} onChange={e => setRefreshIntervalMs(parseInt(e.target.value, 10))}>
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
              <option value={60000}>1m</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end space-x-2 relative">
          <button className="border-2 border-[#FF5722] text-[#FF5722] px-4 py-2 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200 text-sm" onClick={handleReview}>
            Review Filter
          </button>
          <button className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:opacity-90 transition-colors duration-200 text-sm flex items-center" onClick={() => setDownloadMenuOpen(v => !v)}>
            Download Logs
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {downloadMenuOpen && (
            <div className="absolute right-0 top-10 bg-white border rounded shadow z-10 w-40">
              <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => handleDownload('csv')}>CSV</button>
              <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => handleDownload('excel')}>Excel</button>
              <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => handleDownload('pdf')}>PDF</button>
            </div>
          )}
          <button className="border-2 border-[#FF5722] text-[#FF5722] px-4 py-2 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200 text-sm" onClick={handleResetFilters}>
            Reset Filter
          </button>
        </div>
      </div>

      {/* Review Results */}
      {showReview && (
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-md font-bold">Filtered Results Preview</h2>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-600">{new Date().toLocaleString()}</span>
              <button className="text-sm text-gray-600 hover:underline" onClick={() => setShowReview(false)}>Clear</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reviewLoading && (
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-500" colSpan={6}>Loading...</td>
                  </tr>
                )}
                {!reviewLoading && reviewLogs.length === 0 && (
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-500" colSpan={6}>No results for selected filters.</td>
                  </tr>
                )}
                {!reviewLoading && reviewLogs.map(log => (
                  <tr key={`review-${log.recipient_id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{statusBadge(log.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.sender_id || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-md truncate" title={log.body}>{log.body}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.message_created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.recipient_updated_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Review Pagination */}
          <div className="px-6 py-3 flex items-center justify-between bg-gray-50">
            <div className="text-sm text-gray-600">Page {reviewPage} of {Math.max(1, Math.ceil(reviewTotal / (filters.limit || 25)))}</div>
            <div className="space-x-2">
              <button className="px-3 py-1 border rounded text-sm disabled:opacity-50" disabled={reviewPage <= 1} onClick={() => handleReviewPageChange(Math.max(1, reviewPage - 1))}>Previous</button>
              <button className="px-3 py-1 border rounded text-sm disabled:opacity-50" disabled={reviewPage >= Math.max(1, Math.ceil(reviewTotal / (filters.limit || 25)))} onClick={() => handleReviewPageChange(Math.min(Math.max(1, Math.ceil(reviewTotal / (filters.limit || 25))), reviewPage + 1))}>Next</button>
            </div>
          </div>
        </div>
      )}

      {/* Live Logs Heading */}
      <div className="mb-2 px-1">
        <h2 className="text-md font-bold">SMS Logs - Real Time</h2>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated At</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-500" colSpan={7}>Loading...</td>
                </tr>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-500" colSpan={7}>No logs found.</td>
                </tr>
              )}
              {!loading && logs.map(log => (
                <tr key={log.recipient_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{statusBadge(log.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.sender_id || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-700 max-w-md truncate" title={log.body}>{log.body}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.message_created_at).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(log.recipient_updated_at).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button className="text-[#FF5722] hover:underline" onClick={() => navigator.clipboard.writeText(log.message_id)}>Copy ID</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 py-3 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">Page {filters.page || 1} of {totalPages}</div>
          <div className="space-x-2">
            <button className="px-3 py-1 border rounded text-sm disabled:opacity-50" disabled={(filters.page || 1) <= 1} onClick={() => updateFilter('page', Math.max(1, (filters.page || 1) - 1))}>Previous</button>
            <button className="px-3 py-1 border rounded text-sm disabled:opacity-50" disabled={(filters.page || 1) >= totalPages} onClick={() => updateFilter('page', Math.min(totalPages, (filters.page || 1) + 1))}>Next</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4 text-sm text-red-600">{error}</div>
      )}
    </SMSPageLayout>
  );
};

export default SMSLogsPage;


  